import { AnyAri } from '@atlassian/ari/any-ari';
import { getContentById } from '@databases/content-api/confluence/content';
import {
  CONTENT_EXPANSIONS,
  addContentToList
} from '@databases/content-api/redux/content/actions';
import { selectContentByIdOptional } from '@databases/content-api/redux/content/selectors';
import contentSlice from '@databases/content-api/redux/content/slice';
import { loadAsync } from '@databases/utils/redux/redux';
import { type AnyAction } from 'redux';
import { type ThunkAction } from 'redux-thunk';

import {
  type ConnectionContentWithColumns,
  selectContentColumnStatus,
  selectContentOptional
} from '../selectors';
import connectionsSlice, { type Content } from '../slice';
import { type DefaultRootState } from '../types';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

export const loadContentVersionComment = ({
  ari,
  forceReload
}: {
  ari: string;
  forceReload?: boolean;
}): Dispatcher<Promise<ConnectionContentWithColumns | undefined>> => {
  return async (dispatch, getState) => {
    const content: Content | undefined = getState().connections.content[ari];
    if (!content) {
      return undefined;
    }

    await loadAsync(
      () => selectContentColumnStatus(getState().connections, ari, 'versionComment'),
      () => {
        dispatch(
          connectionsSlice.actions.loadContentColumnPending({
            ari,
            column: 'versionComment'
          })
        );
      },
      async () => {
        const content = selectContentOptional(getState().connections, ari);
        if (!content) {
          throw new Error('Content not found');
        }

        const contentId = AnyAri.parse(ari).resourceId;
        let contentData = selectContentByIdOptional(getState(), { contentId });
        if (!contentData) {
          contentData = await getContentById(contentId, CONTENT_EXPANSIONS);
          if (contentData) {
            dispatch(addContentToList(contentData));
          }
          dispatch(contentSlice.actions.loadContentByIdFinished({ contentId }));
        }

        if (!contentData) {
          return undefined;
        }

        const comment = contentData.history?.lastUpdated?.message;
        const version = contentData.history?.lastUpdated?.number;
        return [version, comment].filter(Boolean).join(' - ');
      },
      (version) => {
        if (version) {
          dispatch(
            connectionsSlice.actions.addColumnValueToContent({
              ari: content.ari,
              columnName: 'versionComment',
              data: {
                type: 'string',
                text: version,
                value: version
              }
            })
          );
        }
        dispatch(
          connectionsSlice.actions.loadContentColumnFinished({
            ari,
            column: 'versionComment'
          })
        );
      },
      (error) => {
        dispatch(
          connectionsSlice.actions.loadContentColumnError({
            ari,
            column: 'versionComment',
            error
          })
        );
      },
      { forceReload }
    );

    return selectContentOptional(getState().connections, ari);
  };
};
